import { getGearSnapPoints } from './gear'
import { DrawingElement, GearElement, Point, RopeElement } from './types'
import { getRopeControlPoints } from './rope-geometry'

export type BoundingBox = { minX: number; minY: number; maxX: number; maxY: number }

export function getElementBoundingBox(element: DrawingElement, gears: GearElement[]): BoundingBox | null {
  if (element.kind === 'gear') {
    return {
      minX: element.x,
      minY: element.y,
      maxX: element.x + element.width,
      maxY: element.y + element.height,
    }
  }
  if (element.kind === 'shape') {
    const x1 = element.x
    const y1 = element.y
    const x2 = element.x + element.width
    const y2 = element.y + element.height
    return {
      minX: Math.min(x1, x2),
      minY: Math.min(y1, y2),
      maxX: Math.max(x1, x2),
      maxY: Math.max(y1, y2),
    }
  }
  if (element.kind === 'freedraw') {
    if (element.points.length < 2) return null
    let minX = element.points[0]
    let minY = element.points[1]
    let maxX = element.points[0]
    let maxY = element.points[1]
    for (let i = 0; i < element.points.length; i += 2) {
      const x = element.points[i]
      const y = element.points[i + 1]
      if (x < minX) minX = x
      if (y < minY) minY = y
      if (x > maxX) maxX = x
      if (y > maxY) maxY = y
    }
    return { minX, minY, maxX, maxY }
  }
  const points = getRopeControlPoints(element, gears)
  if (points.length === 0) return null
  let minX = points[0].x
  let minY = points[0].y
  let maxX = points[0].x
  let maxY = points[0].y
  for (const point of points) {
    if (point.x < minX) minX = point.x
    if (point.y < minY) minY = point.y
    if (point.x > maxX) maxX = point.x
    if (point.y > maxY) maxY = point.y
  }
  return { minX, minY, maxX, maxY }
}

export function getContentBoundingBox(elements: DrawingElement[]): BoundingBox | null {
  const gears = elements.filter((element): element is GearElement => element.kind === 'gear')
  let box: BoundingBox | null = null
  for (const element of elements) {
    const elementBox = getElementBoundingBox(element, gears)
    if (!elementBox) continue
    box = box
      ? {
          minX: Math.min(box.minX, elementBox.minX),
          minY: Math.min(box.minY, elementBox.minY),
          maxX: Math.max(box.maxX, elementBox.maxX),
          maxY: Math.max(box.maxY, elementBox.maxY),
        }
      : elementBox
  }
  return box
}

export function getExportBounds(
  box: BoundingBox,
  padding: number,
): { x: number; y: number; width: number; height: number } {
  return {
    x: Math.floor(box.minX - padding),
    y: Math.floor(box.minY - padding),
    width: Math.ceil(box.maxX - box.minX + padding * 2),
    height: Math.ceil(box.maxY - box.minY + padding * 2),
  }
}

export function boxIntersects(a: BoundingBox, b: BoundingBox): boolean {
  return a.minX <= b.maxX && a.maxX >= b.minX && a.minY <= b.maxY && a.maxY >= b.minY
}

export function normalizeMarquee(start: Point, current: Point): BoundingBox {
  return {
    minX: Math.min(start.x, current.x),
    minY: Math.min(start.y, current.y),
    maxX: Math.max(start.x, current.x),
    maxY: Math.max(start.y, current.y),
  }
}

export function deleteElementsByIds(elements: DrawingElement[], ids: Iterable<string>): DrawingElement[] {
  const idSet = new Set(ids)
  if (idSet.size === 0) return elements

  const deletedGears = elements.filter(
    (element): element is GearElement => element.kind === 'gear' && idSet.has(element.id),
  )

  const snapCache = new Map<string, (snapPointId: string) => Point>()
  for (const gear of deletedGears) {
    const snapPoints = getGearSnapPoints(gear)
    snapCache.set(gear.id, (snapPointId: string) => {
      const snap = snapPoints.find((point) => point.id === snapPointId)
      return snap?.world ?? { x: gear.x + gear.width / 2, y: gear.y + gear.height / 2 }
    })
  }

  return elements.flatMap<DrawingElement>((element) => {
    if (idSet.has(element.id)) return []

    // Detach child gears whose parent was deleted
    if (element.kind === 'gear' && element.parentElementId && idSet.has(element.parentElementId)) {
      const { parentElementId: _p, parentSnapPointId: _s, ...rest } = element
      return [rest as GearElement]
    }

    if (element.kind !== 'rope') return [element]

    let rope: RopeElement = element
    let changed = false

    if (rope.start.mode === 'attached' && idSet.has(rope.start.elementId)) {
      const snapWorld = snapCache.get(rope.start.elementId)
      if (snapWorld) {
        const point = snapWorld(rope.start.snapPointId)
        rope = { ...rope, start: { mode: 'free', x: point.x, y: point.y, fallsToGround: false } }
        changed = true
      }
    }
    if (rope.end.mode === 'attached' && idSet.has(rope.end.elementId)) {
      const snapWorld = snapCache.get(rope.end.elementId)
      if (snapWorld) {
        const point = snapWorld(rope.end.snapPointId)
        rope = { ...rope, end: { mode: 'free', x: point.x, y: point.y, fallsToGround: false } }
        changed = true
      }
    }
    if (rope.contacts.some((contact) => idSet.has(contact.elementId))) {
      rope = { ...rope, contacts: rope.contacts.filter((contact) => !idSet.has(contact.elementId)) }
      changed = true
    }

    return [changed ? rope : element]
  })
}
